'use client';

import pushGTMEvent from '@helpers/analyticsHelpers/pushGTMEvent';
import { useAppSelector } from '@hooks/useAppDispatch';
import { selectMiniCartPreviewIsOpen } from '@slices/miniCartSlice';
import { selectSideNavMenuIsOpen } from '@slices/menuSlice';
import { StyledUspItem, StyledUspItemWrapper, StyledUspLink, StyledUspWrapper } from './Usps.styles';
import useCmsComponents from '@hooks/useCmsComponents';
import type { ComponentAdaptedData } from '@occ/api-client';
import useResponsive from '@hooks/useResponsive';
import Text from '@atoms/Text/Text';

interface Props {
  data: { contentUsps: string };
}

export interface UspComponentAdaptedData extends ComponentAdaptedData {
  contentUspText: string;
  uspLinkUrl?: string;
  uspLinkTarget?: string;
  hideInMobile?: boolean;
}

const Usps = ({ data }: Props) => {
  const isCartPreviewOpen = useAppSelector(selectMiniCartPreviewIsOpen);
  const isSideNavMenuOpen = useAppSelector(selectSideNavMenuIsOpen);

  const { fromDesktop, fromTabletPortrait } = useResponsive();

  const sliceValue = fromDesktop && (!isCartPreviewOpen || !isSideNavMenuOpen) ? 4 : 3;
  const { data: uspsItem } = useCmsComponents(data.contentUsps);

  const trackLinkClick = (uspText: string) => {
    pushGTMEvent({
      event: 'Track',
      category: 'usp_bar',
      action: 'navigation',
      label: uspText,
      value: 0,
    });
  };

  const renderUspContent = (item: UspComponentAdaptedData) => {
    const linkMatch = item.contentUspText.match(/^(.*?)<link>(.*?)<\/link>(.*?)$/);

    if (linkMatch && item.uspLinkUrl) {
      const [, before, linkText, after] = linkMatch;
      return (
        <Text type="body" size="small" color="lighterBlack">
          {before}
          <StyledUspLink
            data-testid="usp-link"
            href={item.uspLinkUrl}
            onClick={() => trackLinkClick(item.contentUspText)}
            target={item.uspLinkTarget === 'true' ? '_blank' : ''}
          >
            {linkText}
          </StyledUspLink>
          {after}
        </Text>
      );
    }

    if (item.uspLinkUrl) {
      return (
        <StyledUspLink
          data-testid="usp-link"
          href={item.uspLinkUrl}
          onClick={() => trackLinkClick(item.contentUspText)}
          target={item.uspLinkTarget === 'true' ? '_blank' : ''}
        >
          <Text type="body" size="small" color="lighterBlack">
            {item.contentUspText}
          </Text>
        </StyledUspLink>
      );
    }

    return (
      <StyledUspItemWrapper>
        <Text type="body" size="small" color="lighterBlack">
          {item.contentUspText}
        </Text>
      </StyledUspItemWrapper>
    );
  };

  const filteredUsps = (uspsItem?.component ?? [])
    .filter((item): item is UspComponentAdaptedData => 'contentUspText' in item)
    .filter((item) => !(!fromTabletPortrait && item.hideInMobile));

  const visibleUsps = fromTabletPortrait ? filteredUsps.slice(0, sliceValue) : filteredUsps.slice(0, 1);

  return (
    <StyledUspWrapper>
      {visibleUsps.map((child, index: number) => (
        <StyledUspItem data-testid="usp-item" key={`${index}-${child.uuid}`}>
          {renderUspContent(child)}
        </StyledUspItem>
      ))}
    </StyledUspWrapper>
  );
};

export default Usps;
